import { createRulesResolver } from "./resolvers";
function normalizeResolver(resolver) {
  if (typeof resolver === "function") {
    return resolver;
  }
  return createRulesResolver();
}
export class FormStore {
  mode;
  reValidateMode;
  resolver;
  fields = /* @__PURE__ */new Map();
  listeners = /* @__PURE__ */new Set();
  revision = 0;
  defaultValues;
  values;
  errors = {};
  touched = {};
  dirty = {};
  isSubmitting = false;
  isSubmitted = false;
  constructor(options = {}) {
    this.mode = options.mode ?? "onSubmit";
    this.reValidateMode = options.reValidateMode ?? "onChange";
    this.resolver = normalizeResolver(options.resolver);
    this.defaultValues = {
      ...(options.defaultValues ?? {})
    };
    this.values = {
      ...this.defaultValues,
      ...(options.values ?? {})
    };
  }
  /** Update options without dropping registered fields. */
  updateOptions(options) {
    if (options.mode !== void 0) {
      this.mode = options.mode;
    }
    if (options.reValidateMode !== void 0) {
      this.reValidateMode = options.reValidateMode;
    }
    if (options.resolver !== void 0) {
      this.resolver = normalizeResolver(options.resolver);
    }
    if (options.defaultValues !== void 0) {
      this.defaultValues = {
        ...options.defaultValues
      };
    }
  }
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  notify() {
    this.revision += 1;
    for (const listener of this.listeners) {
      listener();
    }
  }
  getRevision() {
    return this.revision;
  }
  getRegisteredFieldNames() {
    return Array.from(this.fields.keys());
  }
  register(registration) {
    const existing = this.fields.get(registration.name);
    const entry = {
      ...registration,
      rules: registration.rules ?? existing?.rules,
      defaultValue: registration.defaultValue ?? existing?.defaultValue,
      getRules: registration.getRules ?? existing?.getRules
    };
    this.fields.set(registration.name, entry);
    if (this.values[registration.name] === void 0 && entry.defaultValue !== void 0) {
      this.values = {
        ...this.values,
        [registration.name]: entry.defaultValue
      };
    }
    entry.setValue(this.values[registration.name]);
    return () => {
      const current = this.fields.get(registration.name);
      if (current === entry) {
        this.fields.delete(registration.name);
        this.notify();
      }
    };
  }
  updateFieldOptions(name, options) {
    const field = this.fields.get(name);
    if (!field) {
      return;
    }
    if (options.rules !== void 0) {
      field.rules = options.rules;
    }
    if (options.defaultValue !== void 0) {
      field.defaultValue = options.defaultValue;
    }
  }
  getValues() {
    const next = {
      ...this.values
    };
    for (const [name, field] of this.fields) {
      next[name] = field.getValue();
    }
    return next;
  }
  setValue(name, value, options) {
    this.values = {
      ...this.values,
      [name]: value
    };
    const field = this.fields.get(String(name));
    field?.setValue(value);
    if (options?.shouldDirty) {
      this.dirty = {
        ...this.dirty,
        [name]: true
      };
    }
    if (options?.shouldTouch) {
      this.touched = {
        ...this.touched,
        [name]: true
      };
    }
    this.notify();
  }
  markTouched(name) {
    this.touched = {
      ...this.touched,
      [name]: true
    };
    this.notify();
  }
  markDirty(name) {
    this.dirty = {
      ...this.dirty,
      [name]: true
    };
    this.notify();
  }
  clearErrors(name) {
    if (name) {
      const next = {
        ...this.errors
      };
      delete next[name];
      this.errors = next;
      this.notify();
      return;
    }
    this.errors = {};
    this.notify();
  }
  getFieldError(name) {
    return this.errors[name]?.message;
  }
  getState() {
    const values = this.getValues();
    const errorCount = Object.keys(this.errors).length;
    return {
      values,
      errors: this.errors,
      touched: this.touched,
      dirty: this.dirty,
      isSubmitting: this.isSubmitting,
      isSubmitted: this.isSubmitted,
      isValid: errorCount === 0
    };
  }
  async validateField(name) {
    const values = this.getValues();
    const result = await this.resolver(values, {
      fields: this.buildResolverFields()
    }, {
      names: [name]
    });
    const message = result.errors[name]?.message;
    const next = {
      ...this.errors
    };
    if (message) {
      next[name] = {
        type: "validation",
        message
      };
    } else {
      delete next[name];
    }
    this.errors = next;
    this.notify();
    return !message;
  }
  async validateAll() {
    const values = this.getValues();
    const result = await this.resolver(values, {
      fields: this.buildResolverFields()
    }, {});
    this.errors = result.errors;
    this.notify();
    return Object.keys(this.errors).length === 0;
  }
  shouldValidate(name, trigger) {
    const mode = this.isSubmitted ? this.reValidateMode : this.mode;
    switch (mode) {
      case "all":
        return true;
      case "onChange":
        return trigger === "change";
      case "onBlur":
        return trigger === "blur";
      case "onTouched":
        return trigger === "blur" && Boolean(this.touched[name]);
      case "onSubmit":
        return false;
      default:
        return false;
    }
  }
  async handleFieldChange(name) {
    const field = this.fields.get(name);
    if (!field) {
      return;
    }
    const value = field.getValue();
    this.values = {
      ...this.values,
      [name]: value
    };
    this.dirty = {
      ...this.dirty,
      [name]: true
    };
    if (this.shouldValidate(name, "change")) {
      await this.validateField(name);
    } else {
      this.notify();
    }
  }
  async handleFieldBlur(name) {
    this.markTouched(name);
    if (this.shouldValidate(name, "blur")) {
      await this.validateField(name);
    } else {
      this.notify();
    }
  }
  /** Whether a field's validation message should be shown in the UI (RHF-style). */
  shouldDisplayFieldError(name) {
    const message = this.errors[name]?.message;
    if (!message) {
      return false;
    }
    if (this.isSubmitted) {
      return true;
    }
    switch (this.mode) {
      case "all":
        return Boolean(this.dirty[name] || this.touched[name]);
      case "onChange":
        return Boolean(this.dirty[name]);
      case "onBlur":
      case "onTouched":
        return Boolean(this.touched[name]);
      case "onSubmit":
      default:
        return false;
    }
  }
  async handleSubmit(onValid, onInvalid) {
    this.isSubmitting = true;
    this.isSubmitted = true;
    for (const name of this.fields.keys()) {
      this.touched = {
        ...this.touched,
        [name]: true
      };
    }
    this.notify();
    const valid = await this.validateAll();
    this.isSubmitting = false;
    this.notify();
    if (valid) {
      await onValid(this.getValues());
      return;
    }
    onInvalid?.(this.errors);
  }
  reset(values) {
    const nextDefaults = values ?? {
      ...this.defaultValues,
      ...this.collectFieldDefaults()
    };
    this.values = {
      ...nextDefaults
    };
    this.errors = {};
    this.touched = {};
    this.dirty = {};
    this.isSubmitted = false;
    this.isSubmitting = false;
    for (const field of this.fields.values()) {
      field.setValue(this.values[field.name]);
    }
    this.notify();
  }
  collectFieldDefaults() {
    const defaults = {};
    for (const [name, field] of this.fields) {
      if (field.defaultValue !== void 0) {
        defaults[name] = field.defaultValue;
      }
    }
    return defaults;
  }
  buildResolverFields() {
    const fields = {};
    for (const [name, field] of this.fields) {
      const rules = field.getRules?.() ?? field.rules;
      fields[name] = {
        rules
      };
    }
    return fields;
  }
}